import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { OrganizationApps } from "../../../../../gql/graphql";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { DataTable } from "mantine-datatable";
import { IconKey, IconTrash } from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";

// Re-export for Layout.tsx imports
export { EditApp } from "./EditApp";
export { AppsHome } from "./AppsHome";

export const Apps = () => {
  const navigate = useNavigate();
  const clipboard = useClipboard();
  const { loading, data, refetch } = useQuery<{
    organizationApps: OrganizationApps;
  }>(gql`
    query {
      organizationApps(page: { first: 100 }) {
        nodes {
          id
          name
          icon
          shortDescription
          public
          manifest {
            version
          }
          creator {
            id
            email
            profiles {
              username
            }
          }
        }
      }
    }
  `);
  const [deleteApp] = useMutation<any>(gql`
    mutation ($id: String!) {
      deleteOrganizationApp(id: $id)
    }
  `);
  const [resetAppSecret] = useMutation<any>(gql`
    mutation ($id: String!) {
      resetOrganizationAppSecret(id: $id)
    }
  `);

  const deleteItem = async (id: string) => {
    const confirmed = await new Promise<boolean>((resolve) =>
      modals.openConfirmModal({
        title: "Delete app",
        children: (
          <Text>
            Are you sure you want to delete this app? This action cannot be
            undone.
          </Text>
        ),
        labels: { confirm: "Delete", cancel: "Cancel" },
        confirmProps: { color: "red" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      }),
    );
    if (!confirmed) return;
    try {
      await deleteApp({ variables: { id } });
      await refetch();
    } catch (error) {
      notifications.show({
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to delete app",
        color: "red",
      });
    }
  };

  return (
    <>
      <Button onClick={() => navigate("new")}>Create new app</Button>
      <DataTable
        columns={[
          {
            title: "Name",
            accessor: "name",
            render: (item) => (
              <Anchor component={NavLink} to={`${item.id}`}>
                {item.name}
              </Anchor>
            ),
          },
          { title: "Client ID", accessor: "id" },
          {
            title: "Status",
            accessor: "manifest",
            render: (item) => (
              <Badge
                color={item.manifest ? "green" : "gray"}
                variant="light"
                size="sm"
              >
                {item.manifest ? "Configured" : "Private"}
              </Badge>
            ),
          },
          {
            title: "Creator",
            accessor: "creator",
            render: (item) => (
              <>
                {item.creator.profiles[0]?.username ??
                  item.creator.email ??
                  item.creator.id}
              </>
            ),
          },
          {
            title: "Public",
            accessor: "public",
            render: (item) => <>{item.public ? "Yes" : "No"}</>,
          },
          {
            title: "",
            accessor: "",
            render: (item) => (
              <Stack>
                <Tooltip label="Delete app">
                  <ActionIcon onClick={() => deleteItem(item.id)}>
                    <IconTrash />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Reset app secret">
                  <ActionIcon
                    onClick={async () => {
                      const confirmed = await new Promise<boolean>((resolve) =>
                        modals.openConfirmModal({
                          title: "Reset app secret",
                          children: (
                            <Text>
                              Are you sure you want to reset this app's secret?
                              The current secret will stop working immediately.
                            </Text>
                          ),
                          labels: { confirm: "Reset", cancel: "Cancel" },
                          confirmProps: { color: "red" },
                          onConfirm: () => resolve(true),
                          onCancel: () => resolve(false),
                        }),
                      );
                      if (!confirmed) return;
                      try {
                        const newAppSecret = await resetAppSecret({
                          variables: { id: item.id },
                        }).then((res) => {
                          if (res?.error) {
                            throw new Error(res.error.message);
                          }
                          return res;
                        });
                        modals.open({
                          title: "App secret reset",
                          children: (
                            <Stack>
                              <Text>Your app secret has been reset!</Text>
                              <Group>
                                <Anchor
                                  onClick={() => {
                                    clipboard.copy(
                                      newAppSecret.data
                                        ?.resetOrganizationAppSecret ?? "",
                                    );
                                  }}
                                >
                                  <Text fw={700}>
                                    Click here to copy your new app secret
                                  </Text>
                                </Anchor>
                              </Group>
                              <Text c="red">
                                Please note that this is the only time you will
                                be able to see your app secret, so make sure to
                                copy it somewhere safe.
                                <br />
                                You will be able to reset your app secret at any
                                time through the app settings.
                              </Text>
                            </Stack>
                          ),
                        });
                      } catch (error) {
                        notifications.show({
                          title: "Error",
                          message:
                            error instanceof Error
                              ? error.message
                              : "Failed to reset app secret",
                          color: "red",
                        });
                      }
                    }}
                  >
                    <IconKey />
                  </ActionIcon>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        fetching={loading}
        records={data?.organizationApps.nodes}
      />
    </>
  );
};
